import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { cookieManager } from './utils/cookieManager.js';
import formDataStorage from './utils/formDataStorage.js';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import AutoRegisterForm from './components/auth/AutoRegisterForm';
import FormBuilder from './FormBuilder';
// New US Visa homepage
import UsVisaHome from './components/us/UsVisaHome';
import VnVisaHome from './components/vn/VnVisaHome';
import ApplicationManager from './components/shared/ApplicationManager';
import PointsCenter from './components/shared/PointsCenter';
import RechargeCenter from './components/shared/RechargeCenter';
import AdminUsers from './components/shared/AdminUsers';
import AdminDistributors from './components/admin/AdminDistributors';
import ProvinceCityTest from './test/ProvinceCityTest';
// 🆕 EVUS 查询组件
import EvusQuery from './components/evus/EvusQuery';
// 🆕 EVUS 新注册组件
import EvusEnroll from './components/evus/EvusEnroll';
import VnEvisaForm from './modules/vn-evisa/VnEvisaForm';
import VnEvisaDownload from './modules/vn-evisa/VnEvisaDownload';
import ThaiVisaHome from './components/thai/ThaiVisaHome';
import ThailandArrivalCardForm from './modules/thailand-arrival-card/ThailandArrivalCardForm';
import FlightHome from './components/flight/FlightHome';
import FlightSearchPage from './modules/flight/FlightSearchPage';
import FlightBookingPage from './modules/flight/FlightBookingPage';
import FlightOrdersPage from './modules/flight/FlightOrdersPage';
import FlightAdminPage from './modules/flight/FlightAdminPage';
import NivStatusCheck from './components/ceac/NivStatusCheck';
// 🆕 佣金系统组件
import ApplyDistributor from './components/commission/ApplyDistributor';
import DistributorInfo from './components/commission/DistributorInfo';

const detectSite = () => {
  if (typeof window === 'undefined') return 'us';
  const host = window.location.hostname?.toLowerCase() || '';
  if (host.includes('flight')) return 'flight';
  if (host.includes('vnvisa')) return 'vn';
  if (host.includes('thai')) return 'thai';
  return 'us';
};

// 路由组件包装器
const AppRoutes = ({ applicationId, onLoginSuccess, onRegisterSuccess, onLogout, site }) => {
  const navigate = useNavigate();
  const isVnSite = site === 'vn';

  // 受保护的路由组件
  const ProtectedRoute = ({ children }) => {
    const isLoggedIn = cookieManager.getAuthInfo().isLoggedIn;
    if (isLoggedIn) return children;
    // 带上原目标地址，登录成功后跳回去，避免用户丢失已填的查询条件
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  };

  // 🆕 管理员路由组件（需要管理员权限）
  const AdminRoute = ({ children }) => {
    const isLoggedIn = cookieManager.getAuthInfo().isLoggedIn;
    if (!isLoggedIn) {
      return <Navigate to="/auth/login" replace />;
    }
    // 注意：实际权限检查在 AdminDistributors 组件中进行
    // 这里只检查登录状态，具体的角色验证交给组件内部处理
    return children;
  };

  // 处理历史表单点击 - 现在跳转到个人中心
  const handleHistoryClick = () => {
    navigate('/home');
  };

  return (
    <>

      <Routes>
        {/* 首页路由（按子站分发首页） - 公开访问 */}
        <Route path="/" element={
          site === 'flight' ? <FlightHome /> :
          isVnSite ? <VnVisaHome /> :
          site === 'thai' ? <ThaiVisaHome /> :
          <UsVisaHome />
        } />

        {!isVnSite && (
          <>
            {/* 美国签证信息页（公开访问，无需登录） */}
            <Route path="/interview" element={<UsVisaHome.Page type="interview" />} />
            <Route path="/evus" element={<UsVisaHome.Page type="evus" />} />

            {/* EVUS 查询和注册 - 需要认证 */}
            <Route path="/evus/query" element={
              <ProtectedRoute>
                <EvusQuery />
              </ProtectedRoute>
            } />
            <Route path="/evus/enroll" element={
              <ProtectedRoute>
                <EvusEnroll />
              </ProtectedRoute>
            } />
            <Route path="/status" element={
              <ProtectedRoute>
                <NivStatusCheck />
              </ProtectedRoute>
            } />
          </>
        )}

        {/* 个人中心路由 - 需要认证 */}
        <Route 
          path="/home" 
          element={
            <ProtectedRoute>
              <ApplicationManager />
            </ProtectedRoute>
          } 
        />

        {/* 积分中心 - 需要认证 */}
        <Route
          path="/points"
          element={
            <ProtectedRoute>
              <PointsCenter />
            </ProtectedRoute>
          }
        />

        {/* 充值中心 - 需要认证 */}
        <Route
          path="/recharge"
          element={
            <ProtectedRoute>
              <RechargeCenter />
            </ProtectedRoute>
          }
        />

        {/* 管理后台（简版用户列表） - 需要认证 */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />

        {/* 表单应用路由 - 需要认证 */}
        <Route 
          path="/application/:formId?" 
          element={
            <ProtectedRoute>
              <FormBuilder 
                applicationId={applicationId}
                onLogout={onLogout}
                onHistoryClick={handleHistoryClick}
              />
            </ProtectedRoute>
          } 
        />

        {/* 表单编辑路由 - 需要认证 */}
        <Route 
          path="/form/:formId?" 
          element={
            <ProtectedRoute>
              <FormBuilder 
                applicationId={applicationId}
                onLogout={onLogout}
                onHistoryClick={handleHistoryClick}
              />
            </ProtectedRoute>
          } 
        />

        {/* 测试页面路由 */}
        <Route 
          path="/test/province-city" 
          element={<ProvinceCityTest />} 
        />

        <Route
          path="/vn-evisa"
          element={
            <ProtectedRoute>
              <VnEvisaForm />
            </ProtectedRoute>
          }
        />

        {/* 越南 E-VISA PDF 下载 - 需要认证 */}
        <Route
          path="/vn-evisa-download"
          element={
            <ProtectedRoute>
              <VnEvisaDownload />
            </ProtectedRoute>
          }
        />

        {/* 泰国入境卡路由 - 需要认证 */}
        <Route
          path="/thai-arrival-card"
          element={
            <ProtectedRoute>
              <ThailandArrivalCardForm />
            </ProtectedRoute>
          }
        />

        {/* 🆕 机票子站路由（flight.lvbaobao.space） */}
        {/* 航班查询 - 需要认证（每次查询都会产生供应商费用） */}
        <Route
          path="/flight"
          element={
            <ProtectedRoute>
              <FlightSearchPage />
            </ProtectedRoute>
          }
        />
        {/* 填写订单 - 需要认证 */}
        <Route
          path="/flight/book"
          element={
            <ProtectedRoute>
              <FlightBookingPage />
            </ProtectedRoute>
          }
        />
        {/* 我的机票订单 - 需要认证 */}
        <Route
          path="/flight/orders"
          element={
            <ProtectedRoute>
              <FlightOrdersPage />
            </ProtectedRoute>
          }
        />
        {/* 机票订单详情 - 需要认证 */}
        <Route
          path="/flight/orders/:orderId"
          element={
            <ProtectedRoute>
              <FlightOrdersPage />
            </ProtectedRoute>
          }
        />
        {/* 机票订单管理 - 管理员（组件内校验角色） */}
        <Route
          path="/flight/admin"
          element={
            <AdminRoute>
              <FlightAdminPage />
            </AdminRoute>
          }
        />

        {/* 🆕 佣金系统路由 */}
        {/* 分销商申请页面 - 需要认证 */}
        <Route
          path="/commission/apply"
          element={
            <ProtectedRoute>
              <ApplyDistributor />
            </ProtectedRoute>
          }
        />

        {/* 分销商中心 - 需要认证 */}
        <Route
          path="/commission/info"
          element={
            <ProtectedRoute>
              <DistributorInfo />
            </ProtectedRoute>
          }
        />

        {/* 🆕 管理后台 - 分销商审核 */}
        <Route
          path="/admin/distributors"
          element={
            <AdminRoute>
              <AdminDistributors />
            </AdminRoute>
          }
        />

        {/* 登录路由 - 未认证时显示 */}
        {/* 登录路由 - 简化版本 */}
        <Route 
          path="/auth/login" 
          element={
            <LoginForm 
              onLoginSuccess={onLoginSuccess}
              onNavigateToRegister={() => navigate('/auth/register')}
            />
          } 
        />

        {/* 注册路由 - 未认证时显示 */}
        <Route
          path="/auth/register"
          element={
            cookieManager.getAuthInfo().isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <RegisterForm
                onNavigateToLogin={() => navigate('/auth/login')}
                onRegisterSuccess={onRegisterSuccess}
              />
            )
          }
        />

        {/* 重置密码路由 - 未认证时显示 */}
        <Route
          path="/auth/reset-password"
          element={
            cookieManager.getAuthInfo().isLoggedIn ? (
              <Navigate to="/home" replace />
            ) : (
              <ResetPasswordForm
                onNavigateToLogin={() => navigate('/auth/login')}
              />
            )
          }
        />

        {/* 自动注册路由 - 允许所有用户访问 */}
        <Route
          path="/auth/auto-register"
          element={<AutoRegisterForm />}
        />

        {/* 默认重定向到首页 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// 主应用组件
const App = () => {
  const applicationId = "";
  const [site] = React.useState(() => detectSite());
  const handleLoginSuccess = () => {
    // 登录成功时，不需要设置React state，因为cookieManager已经处理了认证信息
    console.log('✅ 登录成功');
  };

  const handleRegisterSuccess = () => {
    // 注册成功后自动登录，不需要设置React state
    console.log('✅ 注册成功');
  };

  const handleLogout = () => {
    cookieManager.clearAuthCookies();
    formDataStorage.clearAllFormData();
    console.log('✅ 退出登录成功');
  };

  return (
    <Router>
      <div className="App">
        <AppRoutes 
          applicationId={applicationId}
          onLoginSuccess={handleLoginSuccess}
          onRegisterSuccess={handleRegisterSuccess}
          onLogout={handleLogout}
          site={site}
        />
      </div>
    </Router>
  );
};

export default App;
